import { useState, useRef, useEffect } from 'react';
import { useTranslation } from '../../hooks/useTranslation';

interface PreJoinScreenProps {
  defaultName?: string;
  onJoin: (name: string) => void;
  isConnecting?: boolean;
}

export function PreJoinScreen({ defaultName = '', onJoin, isConnecting = false }: PreJoinScreenProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(defaultName);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    async function startPreview() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: videoEnabled,
          audio: audioEnabled,
        });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (err) {
        console.warn('Could not access media devices:', err);
      }
    }
    startPreview();
    return () => {
      streamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, [videoEnabled, audioEnabled]);

  const handleJoin = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    // Stop preview before joining
    streamRef.current?.getTracks().forEach((track) => track.stop());
    onJoin(trimmed);
  };

  return (
    <div className="videocall-prejoin">
      <h2>{t('videocall.preJoinTitle')}</h2>
      <div className="videocall-prejoin__preview">
        {videoEnabled ? (
          <video ref={videoRef} autoPlay playsInline muted />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#6b7280' }}>
            {t('videocall.cameraOff')}
          </div>
        )}
      </div>
      <div className="videocall-prejoin__controls">
        <button
          className={`videocall-controls__btn ${!audioEnabled ? 'videocall-controls__btn--muted' : ''}`}
          onClick={() => setAudioEnabled(!audioEnabled)}
          title={audioEnabled ? t('videocall.muteMic') : t('videocall.unmuteMic')}
        >
          {audioEnabled ? '🎤' : '🔇'}
        </button>
        <button
          className={`videocall-controls__btn ${!videoEnabled ? 'videocall-controls__btn--muted' : ''}`}
          onClick={() => setVideoEnabled(!videoEnabled)}
          title={videoEnabled ? t('videocall.cameraOff') : t('videocall.cameraOn')}
        >
          {videoEnabled ? '📹' : '📷'}
        </button>
      </div>
      <input
        className="videocall-prejoin__name-input"
        type="text"
        placeholder={t('videocall.enterName')}
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && handleJoin()}
      />
      <button
        className="videocall-prejoin__join-btn"
        onClick={handleJoin}
        disabled={!name.trim() || isConnecting}
      >
        {isConnecting ? t('common.loading') : t('videocall.joinCall')}
      </button>
    </div>
  );
}
